// Turns API data + the selected view into what each Control Tower section shows.
// Pure functions — no React, no fetching.
import { EXCEPTION_LABELS, fmtDate } from "../ExcelUploadManager";
import { combineExcel } from "./excelData";
import { FALLBACK_PINS, resolveCoords } from "./constants";

/**
 * Which report data the page shows.
 *   activeView: "inbound" | "outbound" | "all" | "platform" (header switch)
 *   activeTab:  report the shipments table shows ("all" view → one report at a time)
 *   xl:         combined Excel aggregates for the view, or null (platform / missing)
 *   viewMissing: an Excel view is selected but its report isn't uploaded yet
 *   fallback:   view when nothing is selected and no report is uploaded
 */
export function resolveView({ view, tableTab, excelReports, fallback = "platform" }) {
    const firstReport = excelReports.inbound ? "inbound" : excelReports.outbound ? "outbound" : null;
    const activeView = view || firstReport || fallback;
    // Pallets has its own view (PalletsView); the shipment sections aren't shown
    if (activeView === "pallets") return { firstReport, activeView, activeTab: firstReport || "inbound", xl: null, viewMissing: false };
    const activeTab = activeView === "all"
        ? (tableTab && excelReports[tableTab] ? tableTab : firstReport || "inbound")
        : activeView;
    const xl = activeView === "all" ? combineExcel(excelReports)
        : activeView !== "platform" && excelReports[activeView] ? combineExcel({ [activeView]: excelReports[activeView] })
        : null;
    const viewMissing = activeView === "all" ? !firstReport
        : activeView !== "platform" && !excelReports[activeView];
    return { firstReport, activeView, activeTab, xl, viewMissing };
}

const EMPTY_DONUT_TAIL = { excel: "No target date", platform: "Not Departed" };
const zeroDonut = tail => [
    { name:"On Time",     value:0, color:"#10B981" },
    { name:"Delayed",     value:0, color:"#F59E0B" },
    { name:"Exception",   value:0, color:"#EF4444" },
    { name: tail,         value:0, color:"#374151" },
];

/** Card values, exception feed, donut, map pins and lanes for the selected view. */
export function buildDashboard({ data, xl, viewMissing }) {
    const kpis = data?.kpis || {};

    // On-time % is over shipments that have a date to be judged against (on time + delayed)
    const xlScheduled = xl ? xl.onTime + xl.delayed : 0;
    const metNote = xl ? [["inbound", "Received by ERD"], ["outbound", "Delivered by req. date"]]
        .map(([t, label]) => {
            const m = xl.met[t];
            const n = m ? m.onTime + m.late : 0;
            return n ? `${label}: ${m.onTime}/${n} (${Math.round(m.onTime / n * 100)}%)` : null;
        })
        .filter(Boolean).join(" · ") : null;

    // Excel views take shipment KPIs from the report; a missing report shows empty, not platform data
    const shipKpis = xl ? {
        shipmentsInTransit: xl.inTransit,
        onTimeCount: xl.onTime,
        onTimePct: xlScheduled > 0 ? Math.round((xl.onTime / xlScheduled) * 100) : 100,
        delayed: xl.delayed,
        delayedPct: xlScheduled > 0 ? Math.round((xl.delayed / xlScheduled) * 100) : 0,
        exceptions: xl.exceptions,
    } : viewMissing ? {} : kpis;

    const xlAgo = xl ? `report ${fmtDate(xl.latestDate)}` : "";
    const exceptions = xl ? [
        ...Object.entries(xl.exceptionsByCode).map(([code, n]) => ({
            icon:"⚠", label: EXCEPTION_LABELS[code] || code, sub:`${n} record${n !== 1 ? "s" : ""}`, ago: xlAgo, count: n, color:"#EF4444",
        })),
        xl.etaSlipped > 0 && { icon:"🕐", label:"ETA pushed later", sub:"since previous upload", ago: xlAgo, count: xl.etaSlipped, color:"#F97316" },
        xl.missing > 0 && { icon:"❓", label:"Missing from latest report", sub:"were in previous upload", ago: xlAgo, count: xl.missing, color:"#F59E0B" },
        xl.statusChanged > 0 && { icon:"↻", label:"Status changed", sub:"since previous upload", ago: xlAgo, count: xl.statusChanged, color:"#3B82F6" },
    ].filter(Boolean).slice(0, 6) : viewMissing ? [] : (data?.exceptions || []);

    const donutData = viewMissing ? zeroDonut(EMPTY_DONUT_TAIL.excel)
        : xl ? [
            { name:"On Time",     value: xl.onTime,      color:"#10B981" },
            { name:"Delayed",     value: xl.delayed,     color:"#F59E0B" },
            { name:"Exception",   value: xl.exceptions,  color:"#EF4444" },
            { name:"No target date", value: xl.noEta,    color:"#374151" },
        ]
        : data?.donut || zeroDonut(EMPTY_DONUT_TAIL.platform);
    const donutTotal = donutData.reduce((s, d) => s + d.value, 0);

    // Map pins
    const rawLocs = xl ? xl.mapLocations || [] : viewMissing ? [] : data?.mapLocations || [];
    const resolvedPins = (rawLocs.length > 0 || xl || viewMissing ? rawLocs : FALLBACK_PINS)
        .map(p => ({ ...p, coords: resolveCoords(p.name) }))
        .filter(p => p.coords);

    // Excel data: real origin → destination lanes, weighted by shipment count.
    // Platform data has no lanes, so keep the previous pin-to-pin lines there.
    const routes = [];
    if (xl) {
        const maxLane = Math.max(1, ...xl.lanes.map(l => l.count));
        xl.lanes.forEach(l => {
            const a = resolveCoords(l.from), b = resolveCoords(l.to);
            if (a && b) routes.push({ positions: [a, b], color: l.color, weight: 1 + 4 * (l.count / maxLane), label: `${l.from} → ${l.to}`, count: l.count });
        });
    } else {
        for (let i = 0; i < resolvedPins.length - 1; i++) {
            routes.push({ positions: [resolvedPins[i].coords, resolvedPins[i + 1].coords], color: "#3B82F6", weight: 1 });
        }
    }
    const unmapped = rawLocs.filter(p => !resolveCoords(p.name)).map(p => p.name);

    return { kpis, shipKpis, metNote, exceptions, donutData, donutTotal, rawLocs, resolvedPins, routes, unmapped };
}

/** Bottom KPI row: report volumes for Inbound / Outbound / All, platform counts otherwise. */
export function bottomKpis({ activeView, xl, kpis }) {
    const vt = xl?.totals || {};
    const fmtNum = v => (v == null ? null : Number(v).toLocaleString("en-AU", { maximumFractionDigits: 2 }));
    const IN_SUB = "Inbound report", OUT_SUB = "Outbound report";
    const VIEW_BOTTOM = {
        inbound: [
            { label:"PO Lines",        value: fmtNum(vt.poLines),    sub: IN_SUB, color:"#10B981" },
            { label:"Unique POs",      value: fmtNum(vt.uniquePOs),  sub: IN_SUB, color:"#3B82F6" },
            { label:"Containers",      value: fmtNum(vt.containers), sub: IN_SUB, color:"#8B5CF6" },
            { label:"Units (Qty)",     value: fmtNum(vt.units),      sub: IN_SUB, color:"#F97316" },
            { label:"Cartons",         value: fmtNum(vt.cartons),    sub: IN_SUB, color:"#10B981" },
            { label:"CBM",             value: fmtNum(vt.cbm),        sub: IN_SUB, color:"#F59E0B" },
        ],
        outbound: [
            { label:"Departed Consignments", value: fmtNum(vt.departed),    sub: OUT_SUB, color:"#10B981" },
            { label:"Prebookings",           value: fmtNum(vt.prebookings), sub: OUT_SUB, color:"#3B82F6" },
            { label:"Returns",               value: fmtNum(vt.returns),     sub: OUT_SUB, color:"#8B5CF6" },
            { label:"Cartons (prebooked)",   value: fmtNum(vt.outCartons),  sub: OUT_SUB, color:"#F97316" },
            { label:"Pallets (est.)",        value: fmtNum(vt.pallets),     sub: OUT_SUB, color:"#10B981" },
            { label:"ASN Flagged",           value: fmtNum(vt.asnFlagged),  sub: OUT_SUB, color:"#EF4444" },
        ],
        all: [
            { label:"PO Lines",              value: fmtNum(vt.poLines),     sub: IN_SUB,  color:"#10B981" },
            { label:"Containers",            value: fmtNum(vt.containers),  sub: IN_SUB,  color:"#8B5CF6" },
            { label:"Units (Qty)",           value: fmtNum(vt.units),       sub: IN_SUB,  color:"#F97316" },
            { label:"Departed Consignments", value: fmtNum(vt.departed),    sub: OUT_SUB, color:"#3B82F6" },
            { label:"Prebookings",           value: fmtNum(vt.prebookings), sub: OUT_SUB, color:"#10B981" },
            { label:"ASN Flagged",           value: fmtNum(vt.asnFlagged),  sub: OUT_SUB, color:"#EF4444" },
        ],
    };
    return VIEW_BOTTOM[activeView] || [
        { label:"Total ASNs",            value: kpis.totalASNs,           sub:"All time",        color:"#10B981" },
        { label:"Total Shipments",       value: kpis.totalShipments,      sub:"All time",        color:"#3B82F6" },
        { label:"POs Awaiting Dispatch", value: kpis.posAwaitingDispatch, sub:"Active",          color:"#8B5CF6" },
        { label:"Total POs",             value: kpis.totalPOs,            sub:"All time",        color:"#F97316" },
        { label:"Inventory SKUs",        value: kpis.totalSKUs,           sub:"In system",       color:"#10B981" },
        { label:"ASNs Awaiting Receipt", value: kpis.asnsAwaitingReceipt, sub:"In transit",      color:"#F59E0B" },
    ];
}
